/**
 * Traducción de los problemas de consumo a algo accionable (B6 / RES-34).
 *
 * El backend anota en el marcador del pedido qué no pudo aplicar, con códigos estables. Acá se
 * convierten en un texto entendible y en los links a la pantalla donde se corrige: el insumo del
 * inventario o el producto de la carta.
 */

/** Códigos que el motor de consumo puede anotar. */
export const CONSUMPTION_ERROR_CODES = [
  'ITEM_NOT_FOUND',
  'UNKNOWN_UNIT',
  'UNIT_MISMATCH',
  'CONVERSION_ERROR',
  'CLAMPED_TO_ZERO',
  'COMBO_COMPONENT_NOT_FOUND',
  'COMBO_OPTION_UNRESOLVED',
  'COMBO_SLOTS_MISSING',
];

// RES-71 (TC-INVB-22): solo estos códigos son sobre un combo roto. MenuPage.handleOpenEditItem
// manda un combo con plantilla (comboItems.templateId) al wizard y todo lo demás al formulario
// clásico — acá no tenemos el item cargado para replicar esa condición exacta, así que para estos
// códigos vamos directo al wizard: si el combo no tiene plantilla (legacy), ComboWizardPage ya
// sabe mostrar el aviso "legacyNotice" y linkear de vuelta al formulario clásico (no es un
// callejón sin salida, ver ComboWizardPage.js ~L1357).
const COMBO_ERROR_CODES = [
  'COMBO_COMPONENT_NOT_FOUND',
  'COMBO_OPTION_UNRESOLVED',
  'COMBO_SLOTS_MISSING',
];

/**
 * Texto de un problema de consumo.
 * @param {Object} error - Error anotado por el motor
 * @param {Function} t - Traductor de i18next
 * @returns {string} Texto
 */
export function describeConsumptionError(error, t) {
  const code = error?.code;
  const key = CONSUMPTION_ERROR_CODES.includes(code)
    ? `inventory.consumptionIssues.errors.${code}`
    : 'inventory.consumptionIssues.errors.UNKNOWN';
  return t(key, {
    inventoryItemId: error?.inventoryItemId || '',
    sourceItemId: error?.sourceItemId || '',
    componentItemId: error?.componentItemId || '',
    requested: error?.requested ?? '',
    applied: error?.applied ?? '',
    shortfall: error?.shortfall ?? '',
    detail: error?.detail || '',
  });
}

/**
 * Links a donde se corrige el problema: el insumo del inventario y/o el producto de la carta.
 * @param {Object} error - Error anotado por el motor
 * @returns {Array<{to: string, labelKey: string}>} Destinos
 */
export function issueTargets(error) {
  const targets = [];
  if (error?.inventoryItemId) {
    // El historial del insumo es la pantalla donde se ve qué pasó y desde donde se corrige
    targets.push({
      to: `/inventory/movements?itemId=${error.inventoryItemId}`,
      labelKey: 'inventory.consumptionIssues.openItem',
    });
  }
  // El producto editable es el del pedido (`sourceItemId`). `componentItemId` solo aparece en
  // COMBO_COMPONENT_NOT_FOUND y es justamente el componente que ya NO existe en la carta:
  // linkearlo abría el formulario de alta vacío y el combo, que es lo que hay que corregir,
  // quedaba inalcanzable (RES-57).
  const menuItemId = error?.sourceItemId || error?.componentItemId;
  if (menuItemId) {
    const to = COMBO_ERROR_CODES.includes(error?.code)
      ? `/menu/combos/${menuItemId}/edit`
      : `/menu/items/edit/${menuItemId}`;
    targets.push({ to, labelKey: 'inventory.consumptionIssues.openProduct' });
  }
  return targets;
}

/**
 * Cantidad de consumos con problemas, para el contador del dock de alertas.
 * @param {Array<Object>} consumptions - Consumos
 * @returns {number} Cantidad
 */
export function issuesCount(consumptions) {
  return (Array.isArray(consumptions) ? consumptions : []).filter(
    (consumption) => consumption?.status === 'partial'
  ).length;
}
